import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState
} from "react";
import { RefreshTokenResponse } from "../../../models/login";
import {
  addParamArrToUrl,
  addParamToUrl,
  API,
  HttpMethods,
  responseRange
} from "../../../services/api-services";
import AppContext from "../../../services/app-context-services";
import AppNotifCtx from "../../../services/notif-context-services";
import {
  convertUtmCookiesToParams,
  getUTMCookies
} from "../../../services/cookie-service";
import { MessageResponse } from "./fetch-data";

type FetchDataStates = "1st-trial" | "2nd-trial" | "refresh";


export interface FetchDataProps<T extends MessageResponse> {
  url: string;
  method: HttpMethods;
  body?: BodyInit;
  headers?: HeadersInit;
  onSuccessful?: (data: T) => void;
  on401?: (response: Response) => void;
  onNotSuccessful?: (response: Response) => void;
  onError?: (reason: any) => void;
  onFinally?: () => void;
  pendingHandlerSetter?: Dispatch<SetStateAction<boolean>>;
  hidePending?: boolean;
  showClientSideError?: boolean;
  showSuccess?: boolean;
  unAuthorized?: boolean;
  admin?: boolean;
  with_dg_id?: boolean;
  counter?: number;
  isTextResponse?: boolean;
  forcedToken?: string;
}

interface UseFetchDataProps<T extends MessageResponse> extends FetchDataProps<T> {
  enabled: boolean;
}

function useFetchData<T extends MessageResponse>({
  enabled,
  onSuccessful,
  onNotSuccessful,
  onError,
  on401,
  onFinally,
  method,
  url,
  pendingHandlerSetter,
  hidePending,
  body,
  headers,
  showClientSideError,
  showSuccess,
  unAuthorized,
  admin,
  with_dg_id = true,
  counter,
  isTextResponse,
  forcedToken
}: UseFetchDataProps<T>) {
  const appCtx = useContext(AppContext);
  const { login, logout, dg_id, isLoggedIn } = appCtx;
  const notifCtx = useContext(AppNotifCtx);
  const [myState, setMyState] = useState<FetchDataStates>("1st-trial");

  useEffect(() => {
    if (!appCtx || !enabled) return;
    if (!unAuthorized && !isLoggedIn && !forcedToken) return;
    if (!hidePending && !pendingHandlerSetter) {
      // notifCtx.showNotif({
      //   notifType: "pending"
      // });
    } else if (pendingHandlerSetter) {
      pendingHandlerSetter(true);
    }

    //             __               _
    //   _ __ ___ / _|_ __ ___  ___| |__
    //  | '__/ _ \ |_| '__/ _ \/ __| '_ \
    //  | | |  __/  _| | |  __/\__ \ | | |
    //  |_|  \___|_| |_|  \___||___/_| |_|

    if (myState === "refresh") {
      if (!appCtx.refreshToken) {
        logout();
        return;
      }

      API.fetch(API.request_refresh_token, {
        method: "POST",
        body: JSON.stringify({
          refresh_token: appCtx.refreshToken
        })
      }).then(response => {
        switch (responseRange(response.status)) {
          case "successful":
            response.json().then((data: RefreshTokenResponse) => {
              login(data.access_token, data.refresh_token, dg_id);
              setMyState("2nd-trial");
            });
            return;
          case "client-error":
            /**
             * response.status === 400: refresh token is expired
             * response.status === 401: device  is expired
             */
            if (response.status === 400 || response.status === 401) {
              logout();
              return;
            }
          default:
            break;
        }
        if (responseRange(response.status) === "successful") {
        }
      });
    } else {
      //  _     _      ___     ____            _
      // / |___| |_   ( _ )   |___ \ _ __   __| |
      // | / __| __|  / _ \/\   __) | '_ \ / _` |
      // | \__ \ |_  | (_>  <  / __/| | | | (_| |
      // |_|___/\__|  \___/\/ |_____|_| |_|\__,_|
      const utmCookies = getUTMCookies();
      const url_with_UTM = utmCookies
        ? addParamArrToUrl(url, convertUtmCookiesToParams(utmCookies))
        : url;
      const url_with_dg_id =
        with_dg_id && dg_id
          ? addParamToUrl(url_with_UTM, "dg_id", dg_id)
          : url_with_UTM;

      const baseFetch = unAuthorized
        ? API.fetch(url_with_dg_id, {
            method: method,
            body: body,
            headers: headers
          })
        : API.methodAuth<T>(method, {
            url: url_with_dg_id,
            token: admin ? appCtx.adminToken : forcedToken || appCtx.token,
            requestInfo: {
              method: method,
              body: body,
              headers: headers
            }
          });
      baseFetch
        .then(response => {
          // console.log('jsonData is:', jsonData);

          switch (responseRange(response.status)) {
            case "successful": {
              !!isTextResponse
                ? response
                    .text()
                    .then((data: string) => {
                      onSuccessful && onSuccessful(data as unknown as T);
                    })
                    .catch(e => console.log(e))
                : response
                    .json()
                    .then((data: T) => {
                      onSuccessful && onSuccessful(data);
                      showSuccess &&
                        notifCtx.showNotif({
                          notifType: "success",
                          message: data.message
                        });
                    })
                    .catch(e => console.log(e));
              break;
            }
            case "client-error":
              if (response.status === 401 && on401) {
                on401(response);
                return;
              } else if (
                response.status === 401 &&
                appCtx.token &&
                !appCtx.refreshToken
              ) {
                logout();
                return;
              } else if (
                response.status === 401 &&
                appCtx.token &&
                myState === "1st-trial"
              ) {
                setMyState("refresh");
                return;
              } else if (response.status === 401 && myState === "2nd-trial") {
                onNotSuccessful && onNotSuccessful(response);
                return;
              } else if (showClientSideError) {
                let message = "";
                switch (response.status) {
                  case 429:
                    message = "تعداد درخواست بیشتر از حد مجاز";
                    break;
                  case 404:
                      message = "مورد یافت نشد."
                      break;                
                  default:
                    break;
                }
                response.json().then((errorResponse: { message: string }) =>
                  notifCtx.showNotif({
                    message: errorResponse.message || message,
                    notifType: "error"
                  })
                );
                onNotSuccessful && onNotSuccessful(response);
                return;
              } else {
                onNotSuccessful && onNotSuccessful(response);
              }
            default:
              onNotSuccessful && onNotSuccessful(response);
              break;
          }
          notifCtx.clearNotif();
        })
        .catch(reason => {
          // console.log("THERE WERE ERROR");
          if (onError) {
            onError(reason);
          } else {
            notifCtx.showNotif({
              notifType: "error",
              message: "خطا در برقراری ارتباط"
            });
          }
        })
        .finally(() => {
          pendingHandlerSetter && pendingHandlerSetter(false);
          onFinally && onFinally();
        });
    }
  }, [myState, counter, url, enabled]); // end of useEffect

}

export default useFetchData;
